import {
  Icon,
  OutlinedInput,
  Scrollbar,
  usePopover,
} from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import {
  Box,
  Button,
  Card,
  Divider,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { cloneElement, memo, ReactElement, useState } from 'react';
import { useSeeAllPopupSearchResult } from './hooks/use-see-all-popup-search-result';
import * as Styled from './styles';
import * as SX from './sx';
import type { SeeAllPopupProps as Props, SeeAllPopupItems } from './types';

const ANCHOR_ORIGIN = {
  vertical: 'center',
  horizontal: 'left',
} as const;
const TRANSFORM_ORIGIN = {
  vertical: 'center',
  horizontal: 'right',
} as const;

export const SeeAllPopup = memo(
  ({
    className,
    items,
    header,
    placeholder,
    title,
    buttonTitle,
    icon,
    ...other
  }: Props) => {
    const popoverState = usePopover();
    const [searchText, setSearchText] = useState('');

    const result: SeeAllPopupItems[] = useSeeAllPopupSearchResult(
      items,
      searchText
    );

    const handleSearch: TextFieldProps['onChange'] = (e) => {
      setSearchText(e.target.value);
    };

    return (
      <Styled.Wrapper
        className={classnames('SeeAllPopup', className)}
        sx={SX.root}
        state={popoverState}
        icon={
          <Icon
            name={icon || 'product-and-services'}
            size={24}
            color="primary.400"
          />
        }
        title={
          <Typography variant="body2" fontWeight={600} color="grey.700">
            {title}
          </Typography>
        }
        element={
          <Stack alignItems="flex-start">
            <Button variant="text" color="primary" size="xsmall">
              {buttonTitle || 'Show More'}
            </Button>
          </Stack>
        }
        anchorOrigin={ANCHOR_ORIGIN}
        transformOrigin={TRANSFORM_ORIGIN}
        contentSx={SX.content}
        {...other}
      >
        <Divider />
        {header}

        <Box px={1} pt={0.5}>
          <OutlinedInput
            fullWidth
            size="small"
            autoComplete="off"
            placeholder={placeholder || 'Search'}
            startIcon={'search'}
            value={searchText}
            onChange={handleSearch}
          />
        </Box>

        <Card variant="soft" sx={SX.itemCard}>
          <Scrollbar rootSx={SX.scrollbar}>
            <Box sx={SX.itemWrapper}>
              {result.length > 0 ? (
                result.map((x) =>
                  cloneElement(x.element as ReactElement, { key: x.key })
                )
              ) : (
                <Stack
                  alignItems="center"
                  sx={SX.noResultFoundWrapper}
                  gap={3}
                  justifyContent="center"
                >
                  <Box sx={SX.emptyIconBox}>
                    <Stack sx={SX.emptyIconWrapper}>
                      <Icon name="laptop" color="grey.400" size={24} />
                    </Stack>
                  </Box>

                  <Box sx={SX.noDataTitleBox}>
                    <Box sx={SX.noDataTitleBox1} />
                    <Box sx={SX.noDataTitleBox2} />
                    <Stack
                      sx={SX.noDataTitleWrapper}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography
                        variant="body4"
                        color="grey.500"
                        fontWeight={500}
                      >
                        No Result Found
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Box>
          </Scrollbar>
        </Card>
      </Styled.Wrapper>
    );
  }
);

SeeAllPopup.displayName = 'SeeAllPopup';
